/**
 * ClientListSkeleton Component
 * 
 * Shows animated placeholder rows while the client list is loading.
 * Mimics the actual table structure for a seamless loading experience.
 */

import React from 'react';

// Single skeleton row that mimics a client row
const SkeletonRow = ({ index }) => (
  <div 
    className="flex items-center gap-4 p-4 border-b animate-pulse"
    style={{ animationDelay: `${index * 50}ms` }}
  >
    {/* Avatar placeholder */}
    <div className="w-10 h-10 bg-gray-200 rounded-full flex-shrink-0" />
    
    {/* Name & details */}
    <div className="flex-1 space-y-2">
      <div className="h-4 bg-gray-200 rounded w-36" />
      <div className="h-3 bg-gray-100 rounded w-24" />
    </div>
    
    {/* DOB column */}
    <div className="hidden md:block w-24">
      <div className="h-4 bg-gray-200 rounded w-20" />
    </div>
    
    {/* Insurance column */}
    <div className="hidden lg:block w-32">
      <div className="h-4 bg-gray-200 rounded w-28" />
    </div>
    
    {/* Status badge */}
    <div className="w-20">
      <div className="h-6 bg-gray-200 rounded-full w-16" />
    </div>
    
    {/* Actions */}
    <div className="flex gap-2">
      <div className="h-8 w-8 bg-gray-200 rounded" />
      <div className="h-8 w-8 bg-gray-200 rounded" />
    </div>
  </div>
);

// Card view skeleton (for grid mode)
const SkeletonCard = ({ index }) => (
  <div 
    className="p-4 border rounded-lg animate-pulse"
    style={{ animationDelay: `${index * 50}ms` }}
  >
    <div className="flex items-start gap-3">
      {/* Avatar */}
      <div className="w-12 h-12 bg-gray-200 rounded-full flex-shrink-0" />
      
      <div className="flex-1 space-y-2">
        {/* Name */}
        <div className="h-4 bg-gray-200 rounded w-32" />
        {/* DOB */}
        <div className="h-3 bg-gray-100 rounded w-24" />
        {/* Insurance */}
        <div className="h-3 bg-gray-100 rounded w-28" />
      </div>
      
      {/* Status badge */}
      <div className="h-6 bg-gray-200 rounded-full w-16" />
    </div>
    
    {/* Provider info */}
    <div className="mt-3 pt-3 border-t">
      <div className="h-3 bg-gray-100 rounded w-40" />
    </div>
  </div>
);

// Header skeleton
const SkeletonHeader = () => (
  <div className="flex items-center justify-between p-4 border-b bg-gray-50 animate-pulse">
    <div className="flex items-center gap-4">
      <div className="h-5 bg-gray-200 rounded w-24" />
      <div className="h-5 bg-gray-200 rounded w-20" />
      <div className="h-5 bg-gray-200 rounded w-28" />
    </div>
    <div className="flex items-center gap-2">
      <div className="h-8 bg-gray-200 rounded w-32" />
      <div className="h-8 bg-gray-200 rounded w-24" />
    </div>
  </div>
);

// Filter bar skeleton
const SkeletonFilters = () => (
  <div className="flex items-center gap-4 p-4 animate-pulse">
    <div className="h-9 bg-gray-200 rounded w-32" />
    <div className="h-9 bg-gray-200 rounded w-40" />
    <div className="h-9 bg-gray-200 rounded w-36" />
    <div className="flex-1" />
    <div className="h-9 bg-gray-200 rounded w-48" />
  </div>
);

// Main skeleton component
export const ClientListSkeleton = ({ 
  rowCount = 10, 
  viewMode = 'list',
  showHeader = true,
  showFilters = true 
}) => {
  return (
    <div className="bg-white rounded-lg border">
      {/* Header skeleton */}
      {showHeader && <SkeletonHeader />}
      
      {/* Filters skeleton */}
      {showFilters && <SkeletonFilters />}
      
      {/* Content skeleton */}
      {viewMode === 'list' ? (
        // Table/List view
        <div>
          {/* Table header */}
          <div className="flex items-center gap-4 px-4 py-3 border-b bg-gray-50/50 animate-pulse">
            <div className="w-10" />
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded w-16" />
            </div>
            <div className="hidden md:block w-24">
              <div className="h-4 bg-gray-200 rounded w-12" />
            </div>
            <div className="hidden lg:block w-32">
              <div className="h-4 bg-gray-200 rounded w-20" />
            </div>
            <div className="w-20">
              <div className="h-4 bg-gray-200 rounded w-14" />
            </div>
            <div className="w-20">
              <div className="h-4 bg-gray-200 rounded w-16" />
            </div>
          </div>
          
          {/* Rows */}
          {Array.from({ length: rowCount }).map((_, index) => (
            <SkeletonRow key={index} index={index} />
          ))}
        </div>
      ) : (
        // Card/Grid view
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
          {Array.from({ length: rowCount }).map((_, index) => (
            <SkeletonCard key={index} index={index} />
          ))}
        </div>
      )}
      
      {/* Pagination skeleton */}
      <div className="flex items-center justify-between p-4 border-t animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-32" />
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 bg-gray-200 rounded" />
          <div className="h-8 w-20 bg-gray-200 rounded" />
          <div className="h-8 w-8 bg-gray-200 rounded" />
        </div>
      </div>
    </div>
  );
};

// Compact skeleton for smaller areas
export const ClientListSkeletonCompact = ({ rowCount = 5 }) => (
  <div className="space-y-2">
    {Array.from({ length: rowCount }).map((_, index) => (
      <div 
        key={index}
        className="flex items-center gap-3 p-3 border rounded animate-pulse"
        style={{ animationDelay: `${index * 50}ms` }}
      >
        <div className="w-8 h-8 bg-gray-200 rounded-full" />
        <div className="flex-1 space-y-1">
          <div className="h-4 bg-gray-200 rounded w-28" />
          <div className="h-3 bg-gray-100 rounded w-20" />
        </div>
        <div className="h-6 bg-gray-200 rounded-full w-14" />
      </div>
    ))}
  </div>
);

export default ClientListSkeleton;
